import { chatApiClient } from '../api/chatApiClient';
import axios from 'axios';
const ensureRoomId = (roomId)=>{
    const resolved = String(roomId ?? '').trim();
    if (!resolved) {
        throw new Error('[PeopleChat] roomId is required');
    }
    return resolved;
};
/**
 * 채팅 API 핸들러 함수들
 * ChatList와 메시지 관리를 위한 유틸리티 함수들
 */ /**
 * 메시지를 읽음으로 표시
 */ export const markMessageAsRead = async (roomId, messageId)=>{
    try {
        await chatApiClient.markMessageAsRead(ensureRoomId(roomId), messageId);
    } catch (error) {
        console.error('Failed to mark message as read:', error);
        throw error;
    }
};
/**
 * 채팅방의 마지막 메시지 업데이트
 */ export const updateLastMessage = async (roomId, message, timestamp)=>{
    try {
        await chatApiClient.updateChatThreadLastMessage(ensureRoomId(roomId), {
            lastMessage: message,
            lastMessageTime: timestamp
        });
    } catch (error) {
        console.error('Failed to update last message:', error);
        throw error;
    }
};
/**
 * 통합 채팅 목록 가져오기 (/chat-threads/user-threads)
 */ export const fetchDMs = async (page = 0, size = 10, searchTerm)=>{
    try {
        const response = await chatApiClient.getChatThreads(page, size, searchTerm);
        return response.data;
    } catch (error) {
        if (axios.isAxiosError(error) && error.response?.status === 401) {
            console.warn('[PeopleChat] Unauthorized when fetching DM list. Returning empty dataset.');
            return {
                content: [],
                number: page,
                size,
                totalElements: 0,
                totalPages: 0,
                last: true,
                first: page === 0
            };
        }
        console.error('Failed to fetch DMs:', error);
        throw error;
    }
};
/**
 * 새 채팅방 생성
 */ export const createNewDM = async (participantUserIds, isGroupThread = false, initialMessage)=>{
    try {
        const response = await chatApiClient.createChatThread({
            participantUserIds: participantUserIds.map((id)=>String(id)),
            isGroupThread,
            initialMessage
        });
        return response.data;
    } catch (error) {
        console.error('Failed to create new DM:', error);
        throw error;
    }
};
/**
 * 채팅방 삭제
 */ export const deleteDM = async (roomId)=>{
    try {
        await chatApiClient.deleteChatThread(ensureRoomId(roomId));
    } catch (error) {
        console.error('Failed to delete DM:', error);
        throw error;
    }
};
/**
 * 채팅방 나가기
 */ export const leaveChatRoom = async (roomId)=>{
    try {
        await chatApiClient.leaveChatRoom(ensureRoomId(roomId));
    } catch (error) {
        console.error('Failed to leave chat room:', error);
        throw error;
    }
};
/**
 * 채팅방에 사용자 초대
 */ export const inviteUsersToChatRoom = async (roomId, userIds)=>{
    try {
        await chatApiClient.inviteUsersToChatRoom(ensureRoomId(roomId), userIds);
    } catch (error) {
        console.error('Failed to invite users to chat room:', error);
        throw error;
    }
};
/**
 * 채팅방에서 사용자 제거
 */ export const removeUserFromChatRoom = async (roomId, userId)=>{
    try {
        await chatApiClient.removeUserFromChatRoom(ensureRoomId(roomId), userId);
    } catch (error) {
        console.error('Failed to remove user from chat room:', error);
        throw error;
    }
};
/**
 * 특정 채팅방의 메시지 목록 가져오기
 */ export const fetchMessagesForRoom = async (roomId, page = 0, size = 20)=>{
    try {
        const response = await chatApiClient.getMessages(ensureRoomId(roomId), page, size);
        return response.data;
    } catch (error) {
        console.error('Failed to fetch messages for room:', error);
        throw error;
    }
};
/**
 * 메시지 검색 (채팅방 내 메시지 검색)
 */ export const searchMessages = async (roomId, searchTerm, page = 0, size = 20)=>{
    try {
        const response = await chatApiClient.searchMessages(ensureRoomId(roomId), {
            query: searchTerm,
            page,
            size
        });
        return response.data;
    } catch (error) {
        console.error('Failed to search messages:', error);
        throw error;
    }
};
/**
 * 전체 채팅방에서 메시지 검색
 */ export const searchAllMessages = async (searchTerm, page = 0, size = 20)=>{
    try {
        const response = await chatApiClient.searchAllMessages({
            query: searchTerm,
            page,
            size
        });
        return response.data;
    } catch (error) {
        console.error('Failed to search all messages:', error);
        throw error;
    }
};
/**
 * 보류 중인 채팅 요청 목록
 */ export const fetchPendingChatRequests = async ()=>{
    try {
        const response = await chatApiClient.getPendingChatRequests();
        return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
        console.error('Failed to fetch pending chat requests:', error);
        throw error;
    }
};
/**
 * 채팅 요청 승인
 */ export const acceptChatRequest = async (roomId)=>{
    try {
        const numericRoomId = Number(roomId);
        if (!Number.isFinite(numericRoomId)) {
            throw new Error('[PeopleChat] acceptChatRequest requires a numeric roomId');
        }
        await chatApiClient.acceptChatRequest(numericRoomId);
    } catch (error) {
        console.error('Failed to accept chat request:', error);
        throw error;
    }
};
/**
 * 채팅 요청 거절
 */ export const declineChatRequest = async (roomId)=>{
    try {
        const numericRoomId = Number(roomId);
        if (!Number.isFinite(numericRoomId)) {
            throw new Error('[PeopleChat] declineChatRequest requires a numeric roomId');
        }
        await chatApiClient.declineChatRequest(numericRoomId);
    } catch (error) {
        console.error('Failed to decline chat request:', error);
        throw error;
    }
};
/**
 * 사용자 검색 (새 채팅 생성용)
 */ export const searchUsers = async (searchTerm, page = 0, size = 10)=>{
    if (!searchTerm.trim()) {
        return {
            content: [],
            number: 0,
            size,
            totalElements: 0,
            totalPages: 0,
            last: true,
            first: true
        };
    }
    try {
        const response = await chatApiClient.searchUsers(searchTerm, page, size);
        return response.data;
    } catch (error) {
        // Axios 에러인 경우 상세 정보 로깅
        if (axios.isAxiosError(error)) {
            console.error('Failed to search users:', {
                status: error.response?.status,
                statusText: error.response?.statusText,
                data: error.response?.data,
                message: error.message
            });
        } else {
            console.error('Failed to search users:', error);
        }
        throw error;
    }
};
/**
 * ChatThreadDTO를 RoomInfo로 변환하는 유틸리티 함수
 */ export const convertChatThreadToRoomInfo = (chatThread, currentUserId)=>{
    const otherParticipants = chatThread.participants.filter((p)=>p.userId.toString() !== currentUserId);
    const roomName = chatThread.chatRoomName || (chatThread.isGroupThread ? `Room ${chatThread.chatRoomId}` // 그룹 채팅은 별도 이름 설정 필요
     : otherParticipants[0]?.displayName ?? 'Unknown User');
    return {
        roomId: chatThread.chatRoomId.toString(),
        lastMessage: chatThread.lastMessage,
        lastMessageTime: chatThread.lastMessageTime,
        unreadMessageCount: chatThread.unreadMessageCount ?? chatThread.participants.reduce((total, participant)=>total + participant.unreadMessageCount, 0),
        roomName,
        userId: otherParticipants[0]?.userId.toString() ?? '',
        participants: chatThread.participants,
        isGroupThread: chatThread.isGroupThread,
        currentUserStatus: chatThread.currentUserStatus
    };
};
/**
 * 읽지 않은 메시지 수 계산
 */ export const calculateUnreadCount = (roomInfos)=>{
    return Object.values(roomInfos).reduce((total, room)=>total + room.unreadMessageCount, 0);
};
/**
 * 채팅방 목록을 최근 메시지 순으로 정렬
 */ export const sortRoomsByLastMessage = (roomInfos)=>{
    return Object.values(roomInfos).sort((a, b)=>{
        const timeA = new Date(a.lastMessageTime).getTime();
        const timeB = new Date(b.lastMessageTime).getTime();
        return timeB - timeA; // 최신순
    });
};
/**
 * 채팅방 제목 생성 (그룹 채팅용)
 */ export const generateRoomTitle = (participants, currentUserId, maxLength = 30)=>{
    const otherParticipants = participants.filter((p)=>p.userId.toString() !== currentUserId);
    if (otherParticipants.length === 0) return 'Empty Room';
    if (otherParticipants.length === 1) return otherParticipants[0].displayName;
    const names = otherParticipants.map((p)=>p.displayName);
    const joinedNames = names.join(', ');
    if (joinedNames.length <= maxLength) {
        return joinedNames;
    }
    // 이름이 너무 길면 일부만 표시
    const truncated = names.slice(0, 2).join(', ');
    const remaining = names.length - 2;
    return remaining > 0 ? `${truncated} 외 ${remaining}명` : truncated;
};
